import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Loading } from '../../../components/Loading';
import { Button } from '../../../GlobalComponents/Button';
import { ErrorMessage } from '../../../GlobalComponents/ErrorMessage';
import { Flex } from '../../../GlobalComponents/Flex';
import { useAppSelector } from '../../../reduxstore/hooks';
import { storeIdState } from '../../../Store/atoms';
import {
	StoreFieldsFragment,
	StorePickupFieldsFragment,
	TerminalFieldsFragment,
	useStoreQuery
} from '../../../Store/graphql';
import { getStoreOpenStatus } from '../../../Store/utils/getStoreOpenStatus';
import { getNow } from '../../../utils/getNow';
import {
	canSkipProfileStepState,
	isPickupMethodValidState,
	pickupMethodSubmissionAttemptedState
} from '../../atoms';
import { Heading } from '../../components/Heading';
import { PickupMethod } from '../../components/PickupMethod';

export const Pickup: React.FC = () => {
	const navigate = useNavigate();

	// Recoil state
	const storeId = useRecoilValue(storeIdState);
	const canSkipProfileStep = useRecoilValue(canSkipProfileStepState);
	const isPickupMethodValid = useRecoilValue(isPickupMethodValidState);
	const setPickupMethodSubmissionAttempted = useSetRecoilState(
		pickupMethodSubmissionAttemptedState
	);

	const availableCards = useAppSelector(
		state => state.accountReducer.profile.cards
	);

	const { data, error, loading } = useStoreQuery({
		fetchPolicy: 'network-only',
		variables: { storeId }
	});

	// Verifying store open status
	const storeOpenStatus =
		data?.store && getStoreOpenStatus(data?.store, getNow());

	const storeData:
		| (StoreFieldsFragment & StorePickupFieldsFragment)
		| undefined = data?.store;

	// Checking Terminal status for In-Store, Drive-Thru and Parking Lot
	const terminalData: TerminalFieldsFragment[] | undefined =
		storeData?.terminals;

	const inStore =
		storeData?.inStorePickup &&
		terminalData?.find(el => el.termId === 2)?.status === 'CONNECTED';

	
	const driveThru =
		storeData?.driveThruPickup &&
		terminalData?.find(el => el.termId === 3)?.status === 'CONNECTED';
	const parkingLot = storeData?.parkingLotPickup && driveThru;

	const navigateTo = (to: string) => {
		navigate(to);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const BackButton = () =>
		!canSkipProfileStep && (
			<Button
				$width="100%"
				$border="1px solid #574D45"
				$backgroundColor="#574D45"
				$color="#FDFDFD"
				onClick={() => navigateTo('../profile')}
			>
				Back
			</Button>
		);

	const ErrorCard: React.FC<{ message: string }> = ({ message }) => (
		<Flex
			$flexDirection="column"
			$width="100%"
			$rowGap="16px"
			$margin="20px 0 0 0"
		>
			<ErrorMessage dismissible={false} message={message} />
			<BackButton />
		</Flex>
	);

	const renderContent = () => {
		if (loading) return <Loading />;

		if (error || storeOpenStatus !== 'OPEN' || !storeData?.oLOEnabled) {
			const message = error
				? error.message // Display error message if there's an error
				: !storeData?.oLOEnabled
					? 'This store is currently not accepting online orders. Please select a different store.'
					: 'Could not complete checkout as store is closed or about to close. Please select a different store by selecting the store locator.';

			return <ErrorCard message={message} />;
		}

		if (parkingLot || inStore || driveThru) {
			return (
				<>
					<PickupMethod
						driveThru={!!driveThru}
						inStore={!!inStore}
						parkingLot={!!parkingLot}
					/>
					<Flex
						$width="100%"
						$height="fit-content"
						$columnGap="16px"
						$margin="20px 0 0 0"
					>
						<BackButton />
						<Button
							$width="100%"
							$border="1px solid #574D45"
							$backgroundColor="#F5F2F2"
							$color="#574D45"
							onClick={() => {
								setPickupMethodSubmissionAttempted(true);
								if (isPickupMethodValid) {
									navigateTo(
										availableCards.length
											? '../payment-options'
											: '../payment'
									);
								}
							}}
						>
							Continue
						</Button>
					</Flex>
				</>
			);
		}

		return (
			<ErrorCard message="It looks like this store is currently unable to process online orders. Please select a different store to order online, or check if the store is open." />
		);
	};

	return (
		<div className="checkout-pickup w-100">
			<Heading>Pickup Method</Heading>
			{renderContent()}
		</div>
	);
};
